import { useCallback } from "react";
import { useSetAtom } from "jotai";
import { authedAtTimestampAtom } from "../../auth/state";
import {
  clientUserAuthGate,
  requiredAgreementsEnabled,
} from "../../statsig/gates";
import {
  useGetCurrentPatientLazyQuery,
  GetCurrentPatientQueryVariables,
  GetCurrentPatientQuery,
} from "../../generated/graphql";
import { useEnterWaitingRoomCallback } from "../nav/useEnterWaitingRoomCallback";
import { useAppDependenciesContext } from "../../state";
import { useLazyClientRequiresAuthedIntroRoom } from "../introduction-room/auth/useClientRequiresAuthedIntroRoom";

/**
 * Returns a callback that gracefully authorizes the current user and enters the
 * waiting room if successful.
 */
export function useGracefullyAuthorizeCurrentUserCallback() {
  const [getCurrentPatient] = useGetCurrentPatientLazyQuery();
  const [getClientRequiresAuthedIntroRoom] =
    useLazyClientRequiresAuthedIntroRoom();
  const setAuthedAtTimestamp = useSetAtom(authedAtTimestampAtom);
  const enterWaitingRoom = useEnterWaitingRoomCallback();
  const { logger, Statsig } = useAppDependenciesContext();

  return useCallback(
    async ({
      variables,
      getCurrentPatientOverride,
    }: {
      variables: GetCurrentPatientQueryVariables;
      getCurrentPatientOverride?: ReturnType<
        typeof useGetCurrentPatientLazyQuery
      >[0];
    }): Promise<
      [null, Error] | [GetCurrentPatientQuery | "noop" | null | undefined, null]
    > => {
      const { patientShortId } = variables;

      const isClienentUserAuthEnabled = Statsig.checkGate(clientUserAuthGate);
      const isRequiredAgreementsEnabled = Statsig.checkGate(
        requiredAgreementsEnabled,
      );
      if (!isClienentUserAuthEnabled) {
        return ["noop", null] as const;
      }
      const handleSuccess = (
        preferredName: string,
        requiresAuthedIntroRoom: boolean,
      ) => {
        setAuthedAtTimestamp(Date.now());
        if (!requiresAuthedIntroRoom) {
          enterWaitingRoom(preferredName);
        }
      };

      let requiresAuthedIntroRoom = false;
      try {
        const { data: clientRequiresAuthedIntroRoomData } =
          isRequiredAgreementsEnabled
            ? await getClientRequiresAuthedIntroRoom({
                variables: { patientShortId },
              })
            : { data: null };
        requiresAuthedIntroRoom =
          clientRequiresAuthedIntroRoomData?.clientRequiresAuthedIntroRoom
            ?.requireAuth ?? false;
      } catch (err) {
        const error = err as Error;
        logger.warn(
          "Failed to fetch client requires authed intro room",
          { patientShortId },
          error,
        );
      }

      try {
        const { data } = await (getCurrentPatientOverride ?? getCurrentPatient)(
          { variables },
        );

        // Not using the `onCompleted` callback because it executes
        // asynchronously which can result in the intro room briefly flashing
        // before the waiting room
        const preferredName = data?.patientInfo?.preferredName;

        if (!preferredName) {
          const errorMsg = "Failed to get patient's preferred name";
          logger.warn(errorMsg, {
            patientShortId,
          });

          return [null, new Error(errorMsg)] as const;
        }
        handleSuccess(preferredName, requiresAuthedIntroRoom);
        return [data, null] as const;
      } catch (err) {
        const error = err as Error;
        logger.error("Failed to fetch patient", { patientShortId }, error);
        return [null, error] as const;
      }
    },
    [
      Statsig,
      enterWaitingRoom,
      getCurrentPatient,
      logger,
      setAuthedAtTimestamp,
      getClientRequiresAuthedIntroRoom,
    ],
  );
}
